import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useMainStore } from '@/stores';
import { storeToRefs } from 'pinia';
import { AppStorage, StorageKey } from '@/utils/storage';
import { activateScope } from '@/trpc/queryClients';
import { normaliseAsOfQuery, readAsOf } from '@/router/asOfQuery';
import { writeRouteRedirect } from '@/router/writeGuard';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'DefaultFirm',
    component: { render: () => null },
    meta: { requiresAuth: true },
  },
  {
    path: '/404',
    name: '404',
    component: () => import('@/layouts/404.vue'),
    meta: { requiresAuth: false, pageTitle: '404' },
  },
  {
    path: '/no-firms',
    name: 'NoFirms',
    component: () => import('@/layouts/NoFirms.vue'),
    meta: { requiresAuth: false, pageTitle: 'No available firms' },
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/layouts/Login.vue'),
    meta: { requiresAuth: false, pageTitle: 'Login' },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/layouts/ForgotPassword.vue'),
    meta: { requiresAuth: false, pageTitle: 'Forgot password' },
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/layouts/ResetPassword.vue'),
    meta: { requiresAuth: false, pageTitle: 'Reset password' },
  },
  {
    path: '/accept-invite',
    name: 'AcceptInvite',
    component: () => import('@/layouts/AcceptInvite.vue'),
    meta: { requiresAuth: false, pageTitle: 'Accept invitation' },
  },
  // Help on a page of its own, opened in a separate window from the help panel. It needs a
  // login but no firm, since help content is the same for every firm.
  {
    path: '/help',
    name: 'HelpPageHome',
    component: () => import('@/layouts/HelpPage.vue'),
    meta: { requiresAuth: true, pageTitle: 'Help' },
  },
  {
    path: '/help/:kind(article|term)/:id',
    name: 'HelpPage',
    component: () => import('@/layouts/HelpPage.vue'),
    meta: { requiresAuth: true, pageTitle: 'Help' },
  },
  {
    path: '/:firmId',
    redirect: { name: 'FirmDashboard' },
    component: () => import('@/layouts/BaseWindow.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: 'dashboard',
        name: 'FirmDashboard',
        component: () => import('@/layouts/homePages/HomePage.vue'),
      },

      // ─── Portfolio ──────────────────────────────────────────
      {
        path: 'portfolio',
        name: 'PortfolioLanding',
        component: () => import('@/layouts/homePages/PortfolioLanding.vue'),
        meta: { pageTitle: 'Portfolio' },
      },
      {
        path: 'portfolio/company/:id',
        component: () => import('@/layouts/company/CompanyDetail.vue'),
        meta: { pageTitle: 'Company detail' },
        children: [
          {
            path: '',
            name: 'CompanyDetail',
            component: { render: () => null },
          },
          {
            path: 'security/new',
            name: 'SecurityNew',
            component: () => import('@/layouts/security/SecurityDetail.vue'),
            meta: { pageTitle: 'New security' },
          },
          {
            path: 'security/:securityId',
            name: 'SecurityDetail',
            component: () => import('@/layouts/security/SecurityDetail.vue'),
            meta: { pageTitle: 'Security detail' },
          },
        ],
      },
      {
        path: 'portfolio/fund/:id',
        component: () => import('@/layouts/fund/FundDetail.vue'),
        // fundContext marks this route and all its children (FundDetail plus routable
        // overlay children like DistributionDetail/CapitalCallDetail, which inherit
        // the merged meta) as carrying the fund id in params.id, so fundStore keeps
        // the fund loaded while those dialogs are open. New routable children pick
        // this up automatically — no per-name list to maintain.
        meta: { pageTitle: 'Fund detail', fundContext: true },
        children: [
          {
            path: '',
            name: 'FundDetail',
            component: { render: () => null },
          },
          {
            path: 'distribution/:distId',
            name: 'DistributionDetail',
            component: () => import('@/layouts/fund/DistributionDetail.vue'),
            meta: { pageTitle: 'Distribution detail' },
          },
          {
            path: 'capital-call/:callId',
            name: 'CapitalCallDetail',
            component: () => import('@/layouts/capitalCall/CapitalCallDetail.vue'),
            meta: { pageTitle: 'Capital call detail' },
          },
        ],
      },
      {
        path: 'portfolio/fund-group/:id',
        name: 'FundGroupDetail',
        component: () => import('@/layouts/fundGroup/FundGroupDetail.vue'),
        meta: { pageTitle: 'Fund group detail' },
      },

      // ─── Contacts ─────────────────────────────────────────────
      {
        path: 'contact/:id',
        name: 'ContactDetail',
        component: () => import('@/layouts/contact/ContactDetail.vue'),
        meta: { pageTitle: 'Contact detail' },
      },

      // ─── Investors (merged LP Management + Capital Events) ─
      {
        path: 'investors',
        name: 'InvestorsLanding',
        component: () => import('@/layouts/homePages/InvestorsLanding.vue'),
        meta: { pageTitle: 'Investors' },
      },
      {
        path: 'investors/investor/:id',
        name: 'InvestorDetail',
        component: () => import('@/layouts/investor/InvestorDetail.vue'),
        meta: { pageTitle: 'Investor detail' },
      },
      {
        path: 'lp/investor-family/:id',
        name: 'InvestorFamilyDetail',
        component: () => import('@/layouts/investor/InvestorFamilyDetail.vue'),
        meta: { pageTitle: 'Investor family detail' },
      },
      {
        path: 'investors/investor/:id/mailing-lists',
        name: 'InvestorMailingLists',
        component: () => import('@/layouts/investor/InvestorMailingLists.vue'),
        meta: { pageTitle: 'Investor mailing lists', requiresWrite: true, writeFallback: 'InvestorDetail' },
      },
      {
        path: 'lp/investor-family/:id/mailing-lists',
        name: 'InvestorFamilyMailingLists',
        component: () => import('@/layouts/investor/InvestorFamilyMailingLists.vue'),
        meta: { pageTitle: 'Investor family mailing lists', requiresWrite: true, writeFallback: 'InvestorFamilyDetail' },
      },
      // redirects from old routes
      { path: 'lp', redirect: 'investors' },
      { path: 'lp/investor/:id', redirect: (to) => ({ name: 'InvestorDetail', params: { id: to.params.id } }) },
      { path: 'capital-events', redirect: 'investors' },

      // ─── Fundraising ────────────────────────────────────────
      // The module is hidden from the sidebar until it is built, so a direct visit goes to Home.
      // To turn it back on, restore the FundraisingLanding route and drop `hidden` in navModel.ts.
      { path: 'fundraising', redirect: { name: 'FirmDashboard' } },

      // ─── Forecasting ───────────────────────────────────────
      {
        path: 'forecasting',
        name: 'ForecastingLanding',
        component: () => import('@/layouts/homePages/ForecastingLanding.vue'),
        meta: { pageTitle: 'Forecasting' },
      },
      {
        path: 'forecasting/fund/:fundId',
        name: 'ForecastWorkspaceFund',
        component: () => import('@/layouts/forecast/ForecastWorkspace.vue'),
        meta: { pageTitle: 'Forecasting' },
      },
      {
        path: 'forecasting/group/:groupId',
        name: 'ForecastWorkspaceGroup',
        component: () => import('@/layouts/forecast/ForecastWorkspace.vue'),
        meta: { pageTitle: 'Forecasting' },
      },

      // ─── Reports ─────────────────────────────────────────────
      {
        path: 'reports',
        name: 'ReportsLanding',
        component: () => import('@/layouts/homePages/ReportsLanding.vue'),
        meta: { pageTitle: 'Reports' },
      },
      {
        path: 'reports/new',
        name: 'ReportNew',
        component: () => import('@/layouts/reports/ReportPage.vue'),
        meta: { pageTitle: 'New report', requiresWrite: true, writeFallback: 'ReportsLanding' },
      },
      {
        path: 'reports/:reportId',
        name: 'ReportView',
        component: () => import('@/layouts/reports/ReportPage.vue'),
        meta: { pageTitle: 'Report' },
      },
      {
        path: 'reports/:reportId/edit',
        name: 'ReportEdit',
        component: () => import('@/layouts/reports/ReportPage.vue'),
        meta: { pageTitle: 'Design report', requiresWrite: true, writeFallback: 'ReportView' },
      },

      // ─── Administration ────────────────────────────────────
      {
        path: 'admin',
        name: 'AdminLanding',
        component: () => import('@/layouts/homePages/AdminLanding.vue'),
        meta: { pageTitle: 'Administration', requiresFirmAdmin: true },
      },
      {
        path: 'admin/lookup-values',
        name: 'AdminLookups',
        component: () => import('@/layouts/admin/LookupManagement.vue'),
        meta: { pageTitle: 'Lookup values', requiresFirmAdmin: true },
      },
      {
        path: 'admin/custom-fields',
        name: 'AdminCustomFields',
        component: () => import('@/layouts/admin/CustomFieldManagement.vue'),
        meta: { pageTitle: 'Custom fields', requiresFirmAdmin: true },
      },
      {
        path: 'admin/mailing-lists',
        name: 'AdminMailingLists',
        component: () => import('@/layouts/admin/MailingListManagement.vue'),
        meta: { pageTitle: 'Mailing lists', requiresFirmAdmin: true },
      },
      {
        path: 'admin/mailing-lists/:id/coverage',
        name: 'AdminMailingListCoverage',
        component: () => import('@/layouts/admin/MailingListCoverage.vue'),
        meta: { pageTitle: 'Mailing list coverage', requiresFirmAdmin: true },
      },
      {
        path: 'admin/organizations',
        name: 'AdminOrganizations',
        component: () => import('@/layouts/admin/OrganizationManagement.vue'),
        meta: { pageTitle: 'Organizations', requiresFirmAdmin: true },
      },
      {
        path: 'admin/users',
        name: 'AdminUsers',
        component: () => import('@/layouts/admin/UserManagement.vue'),
        meta: { pageTitle: 'User management', requiresFirmAdmin: true },
      },
      {
        path: 'admin/other-settings',
        name: 'AdminOtherSettings',
        component: () => import('@/layouts/admin/OtherSettings.vue'),
        meta: { pageTitle: 'Other settings', requiresFirmAdmin: true },
      },
      {
        path: 'admin/alert-settings',
        name: 'AdminAlertSettings',
        component: () => import('@/layouts/admin/AlertSettings.vue'),
        meta: { pageTitle: 'Alert settings', requiresFirmAdmin: true },
      },
      {
        path: 'admin/firms',
        name: 'AdminFirms',
        component: () => import('@/layouts/admin/FirmManagement.vue'),
        meta: { pageTitle: 'Firm management', requiresGlobalAdmin: true },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from) => {
  const asOfRedirect = normaliseAsOfQuery(to, from);
  if (asOfRedirect)
    return asOfRedirect;

  const mainStore = useMainStore();
  const { loggedIn, isGlobalAdmin } = storeToRefs(mainStore);

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const requiresGlobalAdmin = to.matched.some((record) => record.meta.requiresGlobalAdmin);
  const requiresFirmAdmin = to.matched.some((record) => record.meta.requiresFirmAdmin);

  if (requiresAuth)
    await mainStore.refreshUser();

  if (requiresAuth && !loggedIn.value) {
    return {
      path: '/login',
      query: { redirectTo: to.fullPath },
    };
  }

  const isGlobalAdminRoute = requiresGlobalAdmin;
  if (requiresAuth && mainStore.availableFirms.length === 0 && !(isGlobalAdminRoute && isGlobalAdmin.value)) {
    return { path: '/no-firms' };
  }

  if (
    to.params?.firmId &&
    typeof to.params.firmId === 'string' &&
    !mainStore.availableFirms.find((firm) => firm.id === to.params.firmId)
  ) {
    // Login-time firm preference, not the active scope — that is owned by the route.
    const stored = AppStorage.get(StorageKey.ActiveFirmId);
    const fallbackId = (stored && mainStore.availableFirms.find((f) => f.id === stored))
      ? stored
      : mainStore.availableFirms[0]?.id;
    return { path: `/${fallbackId}/dashboard` };
  }

  if (requiresGlobalAdmin && !isGlobalAdmin.value) {
    return { path: '/404' };
  }
  if (requiresFirmAdmin) {
    const targetFirmId = typeof to.params.firmId === 'string' ? to.params.firmId : '';
    const isAdmin = mainStore.isGlobalAdmin ||
      mainStore.permissions.some((p) => p.firmId === targetFirmId && p.isAdmin);
    if (!isAdmin)
      return { path: '/404' };
  }

  if (typeof to.params.firmId === 'string') {
    const firmId = to.params.firmId;
    const canWrite = mainStore.isGlobalAdmin || mainStore.permissions.some((p) => p.firmId === firmId && p.canWrite);
    const writeRedirect = writeRouteRedirect(to, canWrite);
    if (writeRedirect)
      return writeRedirect;
  }

  if (to.name === 'DefaultFirm') {
    // Login-time firm preference, not the active scope — that is owned by the route.
    const stored = AppStorage.get(StorageKey.ActiveFirmId);
    const preferredId = (stored && mainStore.availableFirms.find((f) => f.id === stored))
      ? stored
      : mainStore.availableFirms[0]?.id;
    return { path: `/${preferredId}` };
  }

  if (to.meta.pageTitle)
    document.title = 'VentureTrack - ' + to.meta.pageTitle;

  return true;
});

router.afterEach((to) => {
  if (typeof to.params.firmId === 'string') {
    // The route is the single source of the query scope.
    activateScope({ firmId: to.params.firmId, asOf: readAsOf(to.query) });
  }

  document.querySelector('.app-shell__content')?.scrollTo(0, 0);
});

export default router;
